import type { DialWelcome, WelcomeAction } from "@keydials/api-client";
import { RichText } from "@keydials/ui";
import { Modal } from "../components/chrome/Modal";

/**
 * KEY-814: a dial's welcome page — what the dial needs to be useful, and buttons straight to it. Shown to an editor
 * or admin the first time they open the dial (and again when a new version changes it), and on demand from the
 * Overview tab's "Getting started" and the Help drawer. Closing it, or pressing any of its buttons, counts as
 * having seen it.
 */
export function WelcomePage({
  welcome,
  dialName,
  actions,
  onAction,
  onClose,
}: {
  welcome: DialWelcome;
  dialName: string;
  /** The buttons this person can use, already filtered. */
  actions: WelcomeAction[];
  onAction: (action: WelcomeAction) => void;
  onClose: () => void;
}) {
  return (
    <Modal title={`Welcome to ${dialName}`} subtitle="Getting started" onClose={onClose} width={560}>
      <div data-testid="welcome-page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <RichText value={welcome.html} style={{ fontSize: 14.5, lineHeight: 1.55, color: "var(--text)" }} />
        {actions.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {actions.map((a, i) => (
              <button
                key={`${a.kind}:${a.target}:${i}`}
                onClick={() => onAction(a)}
                className={i === 0 ? "kd-btn-primary" : "kd-btn-dashed"}
                style={{ fontSize: 13.5, justifyContent: "center" }}
              >
                {a.label}
                {a.kind === "link" && <span aria-hidden="true"> ↗</span>}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
            You can open this again from the dial's Overview, or from Help.
          </span>
          <button onClick={onClose} className="kd-btn-dashed" style={{ fontSize: 13, width: "auto", padding: "8px 16px" }}>
            Got it
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** The Overview tab's way back to the welcome page (KEY-814). */
export function GettingStartedButton({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="kd-btn-dashed"
      style={{ fontSize: 13, width: "auto", padding: "7px 14px", margin: "0 0 12px", alignSelf: "flex-start" }}
    >
      Getting started with this dial
    </button>
  );
}
